/**
 * PlannerPage — Planner (spec §8.3, §5.2 refinements).
 *
 * Seven-day tabs (today + 6), quarter-hour placement on a proportional
 * timeline, compact 06:00–23:00 view with an accessible 24-hour expand
 * control, activity deck in a BottomSheet, interval-correct collision
 * prevention with a next-free-slot suggestion, edit sheet (activity, date,
 * time, duration, note — note saves on blur and Enter), accessible non-drag
 * move/resize, delete with confirm, read-only medication markers, optimistic
 * writes with rollback and honest SyncStatus per save.
 */
import { useMemo, useState } from 'react'
import {
  Banner,
  Button,
  ConfirmDestructiveAction,
  EmptyState,
  ErrorState,
  Skeleton,
  useToast,
} from '@/components/ui'
import { PlusIcon } from '@/components/icons'
import { PageHeader } from '@/components/navigation'
import { repositories } from '@/services/repositories'
import { getActivityById, ACTIVITY_CATEGORIES, type Activity } from '@/data/activities'
import type { PlannerItem } from '@/types'
import { ActivityDeck } from './ActivityDeck'
import { EditItemSheet, type EditSaveResult, type EditValues } from './EditItemSheet'
import { PlannerTimeline } from './PlannerTimeline'
import { findNextFreeSlot, hasCollision, MINUTES_PER_DAY } from './collisions'
import {
  dayLabelFor,
  formatDuration,
  formatMinutes,
  localToday,
  nextSevenLocalDates,
} from './plannerDates'
import { usePlannerDay } from './usePlannerDay'
import './planner.css'

const COMPACT_RANGE_START = 6 * 60 // 06:00
const COMPACT_RANGE_END = 23 * 60 // 23:00

type PlacementDraft = {
  activity: Activity
  durationMinutes: number
}

type Feedback = {
  tone: 'error' | 'info'
  message: string
  suggestion?: number
}

export default function PlannerPage() {
  const days = useMemo(() => nextSevenLocalDates(), [])
  const [selectedDate, setSelectedDate] = useState<string>(() => localToday())
  const {
    items,
    occurrences,
    status,
    error,
    syncStates,
    online,
    pendingWrites,
    refresh,
    placeItem,
    updateItem,
    removeItem,
  } = usePlannerDay(selectedDate)
  const { showToast } = useToast()

  const [fullDay, setFullDay] = useState(false)
  const [deckOpen, setDeckOpen] = useState(false)
  const [draft, setDraft] = useState<PlacementDraft | null>(null)
  const [placing, setPlacing] = useState(false)
  const [editTargetId, setEditTargetId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PlannerItem | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [savingEdit, setSavingEdit] = useState(false)
  const [feedback, setFeedback] = useState<Feedback | null>(null)

  const rangeStart = fullDay ? 0 : COMPACT_RANGE_START
  const rangeEnd = fullDay ? MINUTES_PER_DAY : COMPACT_RANGE_END

  const editTarget = editTargetId ? (items.find((item) => item.id === editTargetId) ?? null) : null

  const categoryNameById = useMemo(
    () => new Map(ACTIVITY_CATEGORIES.map((category) => [category.id, category.name])),
    [],
  )

  const cloudUnavailable = status === 'error' && error?.code === 'SUPABASE_NOT_CONFIGURED'
  const hiddenOutsideRange = fullDay
    ? 0
    : items.filter(
        (item) =>
          item.startMinute + item.durationMinutes <= rangeStart || item.startMinute >= rangeEnd,
      ).length

  const handleSelectActivity = (activity: Activity, durationMinutes: number) => {
    setDraft({ activity, durationMinutes })
    setDeckOpen(false)
    setFeedback({
      tone: 'info',
      message: `Tik op een tijdstip in de tijdlijn om “${activity.name}” (${formatDuration(durationMinutes)}) te plaatsen.`,
    })
  }

  const placeAt = async (startMinute: number, current: PlacementDraft) => {
    setPlacing(true)
    const result = await placeItem({
      date: selectedDate,
      startMinute,
      durationMinutes: current.durationMinutes,
      activityId: current.activity.id,
      activityName: current.activity.name,
      category: categoryNameById.get(current.activity.categoryId) ?? current.activity.categoryId,
      color: current.activity.color,
      iconId: current.activity.iconId,
      source: 'manual',
    })
    setPlacing(false)
    if (result.ok) {
      setDraft(null)
      setFeedback({
        tone: 'info',
        message: `“${current.activity.name}” is gepland om ${formatMinutes(startMinute)}.`,
      })
    } else {
      setFeedback({ tone: 'error', message: result.error.userMessage })
    }
  }

  const handleRequestPlace = (startMinute: number) => {
    if (!draft || placing) return
    setFeedback(null)
    if (hasCollision(items, startMinute, draft.durationMinutes)) {
      const suggestion = findNextFreeSlot(items, draft.durationMinutes, { fromMinute: startMinute })
      setFeedback({
        tone: 'error',
        message:
          suggestion !== null
            ? `Deze tijd is al bezet. De volgende vrije plek is om ${formatMinutes(suggestion)}.`
            : 'Deze tijd is al bezet en er is verder geen vrije plek op deze dag.',
        ...(suggestion !== null ? { suggestion } : {}),
      })
      return
    }
    void placeAt(startMinute, draft)
  }

  const handleSaveEdit = async (values: EditValues): Promise<EditSaveResult> => {
    if (!editTarget) return { ok: false, message: 'Deze activiteit bestaat niet meer.' }
    setSavingEdit(true)
    try {
      // Collision check against the target day. When the item moves to
      // another day, that day's items are fetched for the check.
      let checkItems = items
      let suggestion: number | undefined
      if (values.date === selectedDate) {
        if (hasCollision(checkItems, values.startMinute, values.durationMinutes, editTarget.id)) {
          const free = findNextFreeSlot(checkItems, values.durationMinutes, {
            fromMinute: values.startMinute,
            ignoreId: editTarget.id,
          })
          suggestion = free ?? undefined
          return {
            ok: false,
            message:
              free !== null
                ? `Deze tijd overlapt met een andere activiteit. De volgende vrije plek is om ${formatMinutes(free)}.`
                : 'Deze tijd overlapt met een andere activiteit.',
            ...(suggestion !== undefined ? { suggestedStartMinute: suggestion } : {}),
          }
        }
      } else {
        try {
          checkItems = await repositories.planner.listForDate(values.date)
        } catch {
          checkItems = []
        }
        if (hasCollision(checkItems, values.startMinute, values.durationMinutes, editTarget.id)) {
          return { ok: false, message: 'Deze tijd overlapt met een andere activiteit op die dag.' }
        }
      }

      const activity = getActivityById(values.activityId)
      const patch = {
        date: values.date,
        startMinute: values.startMinute,
        durationMinutes: values.durationMinutes,
        notes: values.notes,
        ...(activity
          ? {
              activityName: activity.name,
              category: categoryNameById.get(activity.categoryId) ?? activity.categoryId,
              color: activity.color,
              iconId: activity.iconId,
            }
          : {}),
      }
      const result = await updateItem(editTarget.id, patch)
      if (!result.ok) {
        return { ok: false, message: result.error.userMessage }
      }
      if (values.date !== selectedDate) {
        // The item moved to another day: reload this day so it disappears.
        void refresh()
      }
      setEditTargetId(null)
      showToast({ message: 'Activiteit opgeslagen.', tone: 'success' })
      return { ok: true }
    } finally {
      setSavingEdit(false)
    }
  }

  const handleSaveNote = async (notes: string) => {
    if (!editTarget) return
    const result = await updateItem(editTarget.id, { notes })
    if (!result.ok) {
      showToast({ message: 'Notitie opslaan is mislukt. Probeer het opnieuw.', tone: 'error' })
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget || deleting) return
    setDeleting(true)
    const result = await removeItem(deleteTarget.id)
    setDeleting(false)
    if (result.ok) {
      showToast({ message: 'Activiteit verwijderd.', tone: 'success' })
      setDeleteTarget(null)
    } else {
      showToast({ message: result.error.userMessage, tone: 'error' })
    }
  }

  const selectedDayLabel = dayLabelFor(selectedDate)
  const isEmptyDay = status === 'ready' && items.length === 0 && occurrences.length === 0

  return (
    <div className="planner-page">
      <PageHeader title="Planner" subtitle={selectedDayLabel.long} />

      {!online ? (
        <Banner tone="warning">
          Je bent offline. Wijzigingen worden bewaard en gesynchroniseerd zodra je weer online
          bent.
        </Banner>
      ) : null}
      {pendingWrites > 0 ? (
        <Banner tone="info">
          {pendingWrites === 1
            ? '1 wijziging wacht op synchronisatie.'
            : `${pendingWrites} wijzigingen wachten op synchronisatie.`}
        </Banner>
      ) : null}

      <div
        className="planner-days"
        role="group"
        aria-label="Kies een dag (vandaag en de komende zes dagen)"
      >
        {days.map((date) => {
          const label = dayLabelFor(date)
          const selected = date === selectedDate
          return (
            <button
              key={date}
              type="button"
              className={`planner-days__chip${selected ? ' planner-days__chip--selected' : ''}`}
              aria-label={label.long}
              aria-pressed={selected}
              aria-current={label.isToday ? 'date' : undefined}
              onClick={() => {
                setSelectedDate(date)
                setFeedback(null)
                setDraft(null)
              }}
            >
              {label.short}
            </button>
          )
        })}
      </div>

      {status === 'error' && error ? (
        <ErrorState
          title={cloudUnavailable ? 'Cloudverbinding niet ingesteld' : 'Planner laden mislukt'}
          error={error}
          onRetry={error.retryable ? () => void refresh() : undefined}
        />
      ) : null}
      {cloudUnavailable ? (
        <Banner tone="info">
          De planner heeft een cloudverbinding nodig. Er wordt niets lokaal opgeslagen alsof het in
          de cloud staat.
        </Banner>
      ) : null}

      {status !== 'error' ? (
        <div className="planner-actions">
          <Button
            variant="primary"
            fullWidth
            onClick={() => setDeckOpen(true)}
            disabled={status !== 'ready'}
          >
            <PlusIcon size={18} />
            Activiteit toevoegen
          </Button>
        </div>
      ) : null}

      {draft ? (
        <div className="planner-armed" role="status">
          <span className="planner-armed__text">
            Tik op de tijdlijn om “{draft.activity.name}” ({formatDuration(draft.durationMinutes)})
            te plaatsen.
          </span>
          <Button variant="ghost" size="sm" onClick={() => setDeckOpen(true)}>
            Wijzig
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>
            Annuleren
          </Button>
        </div>
      ) : null}

      {feedback ? (
        <div
          className={`planner-feedback planner-feedback--${feedback.tone}`}
          role={feedback.tone === 'error' ? 'alert' : 'status'}
          aria-live={feedback.tone === 'error' ? 'assertive' : 'polite'}
        >
          <p>{feedback.message}</p>
          {feedback.suggestion !== undefined && draft ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                const suggestion = feedback.suggestion
                setFeedback(null)
                if (suggestion !== undefined) void placeAt(suggestion, draft)
              }}
            >
              Plaats om {formatMinutes(feedback.suggestion)}
            </Button>
          ) : null}
        </div>
      ) : null}

      {status === 'loading' ? <Skeleton lines={5} /> : null}

      {isEmptyDay ? (
        <EmptyState
          title="Plan je dag"
          body="Deze dag is nog leeg. Voeg een activiteit toe om je dag structuur te geven."
          action={
            <Button variant="primary" onClick={() => setDeckOpen(true)}>
              <PlusIcon size={18} />
              Plan je dag
            </Button>
          }
        />
      ) : null}

      {status === 'ready' ? (
        <>
          <div className="planner-range">
            <Button
              variant="ghost"
              size="sm"
              aria-expanded={fullDay}
              onClick={() => setFullDay((value) => !value)}
            >
              {fullDay ? 'Toon compact (06:00–23:00)' : 'Toon hele dag (24 uur)'}
            </Button>
            {hiddenOutsideRange > 0 ? (
              <p className="planner-range__hint" role="note">
                {hiddenOutsideRange === 1
                  ? '1 activiteit valt buiten 06:00–23:00. Toon de hele dag om hem te zien.'
                  : `${hiddenOutsideRange} activiteiten vallen buiten 06:00–23:00. Toon de hele dag om ze te zien.`}
              </p>
            ) : null}
          </div>

          <PlannerTimeline
            items={items}
            occurrences={occurrences}
            syncStates={syncStates}
            rangeStartMinute={rangeStart}
            rangeEndMinute={rangeEnd}
            placementArmed={draft !== null && !placing}
            onRequestPlace={handleRequestPlace}
            onEdit={(item) => setEditTargetId(item.id)}
            onDelete={setDeleteTarget}
          />
        </>
      ) : null}

      <ActivityDeck open={deckOpen} onClose={() => setDeckOpen(false)} onSelect={handleSelectActivity} />

      {editTarget ? (
        <EditItemSheet
          open
          item={editTarget}
          syncState={syncStates[editTarget.id] ?? 'cloud_saved'}
          saving={savingEdit}
          onClose={() => setEditTargetId(null)}
          onSave={handleSaveEdit}
          onSaveNote={handleSaveNote}
        />
      ) : null}

      <ConfirmDestructiveAction
        open={deleteTarget !== null}
        title="Activiteit verwijderen"
        body={
          deleteTarget
            ? `Weet je zeker dat je “${deleteTarget.activityName}” om ${formatMinutes(deleteTarget.startMinute)} wilt verwijderen?`
            : undefined
        }
        confirmLabel={deleting ? 'Verwijderen…' : 'Verwijderen'}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}
